"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useInView } from "motion/react";
import Container from "@/components/ui/Container";
import { MetricCounter } from "@/components/ui/motion";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { useNearViewport } from "@/hooks/useNearViewport";
import TextReveal from "@/components/ui/TextReveal";

/**
 * Live numbers band — quantified credibility.
 *
 * Color-diversified: each card gets a distinct accent to break purple monotony.
 */
type Metric = {
  eyebrow: string;
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  compact?: boolean;
  caption: string;
  /** 8–12 sample points, normalized 0–1, used for the sparkline */
  series: number[];
  accent: string;
  accentFill: string;
};

const METRICS: readonly Metric[] = [
  {
    eyebrow: "Failover",
    value: 500,
    suffix: " ms",
    caption: "Auto-reroute target on regional health drop",
    series: [0.85, 0.78, 0.72, 0.6, 0.55, 0.5, 0.45, 0.4, 0.38, 0.36, 0.34, 0.32],
    accent: "#6C63FF",
    accentFill: "rgba(108,99,255,0.12)",
  },
  {
    eyebrow: "p99 latency",
    value: 87,
    suffix: " ms",
    caption: "Global Median Target",
    series: [0.6, 0.55, 0.62, 0.5, 0.45, 0.48, 0.42, 0.38, 0.4, 0.35, 0.32, 0.3],
    accent: "#00D4FF",
    accentFill: "rgba(0,212,255,0.12)",
  },
  {
    eyebrow: "Chains supported",
    value: 50,
    suffix: "+",
    caption: "EVM, L2s, Cosmos, non-EVM",
    series: [0.3, 0.32, 0.4, 0.42, 0.5, 0.55, 0.6, 0.7, 0.78, 0.85, 0.92, 1.0],
    accent: "#F59E0B",
    accentFill: "rgba(245,158,11,0.12)",
  },
  {
    eyebrow: "Uptime SLA",
    value: 99.95,
    decimals: 2,
    suffix: " %",
    caption: "Contractual · Production Tier",
    series: [0.96, 0.98, 0.97, 0.99, 0.99, 1.0, 0.99, 1.0, 1.0, 0.99, 1.0, 1.0],
    accent: "#10B981",
    accentFill: "rgba(16,185,129,0.12)",
  },
];

/* ── Sparkline that draws in once visible ───────────────────────────────────── */
function Sparkline({ data, accent, accentFill }: { data: number[]; accent: string; accentFill: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (!inView) return;
    const id = setTimeout(() => setDrawn(true), 0);
    return () => clearTimeout(id);
  }, [inView]);

  // Build the path: width 100 × height 30
  const W = 100;
  const H = 30;
  const stepX = W / (data.length - 1);

  const points = data.map((v, i) => [i * stepX, H - v * (H - 4) - 2] as const);
  const linePath = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = `${linePath} L${W},${H} L0,${H} Z`;

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-8 w-full sm:h-10"
      aria-hidden
    >
      {/* Filled area under the line */}
      <path d={areaPath} fill={accentFill} opacity={drawn ? 1 : 0} style={{ transition: "opacity 600ms ease-out 800ms" }} />
      {/* Stroke that draws in */}
      <path
        d={linePath}
        fill="none"
        stroke={accent}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={drawn ? 0 : 1}
        style={{ transition: "stroke-dashoffset 1200ms cubic-bezier(0.23, 1, 0.32, 1)" }}
      />
      {/* End-of-line dot */}
      <circle
        cx={points[points.length - 1][0]}
        cy={points[points.length - 1][1]}
        r="1.6"
        fill={accent}
        opacity={drawn ? 1 : 0}
        style={{ transition: "opacity 300ms ease-out 1000ms" }}
      />
    </svg>
  );
}

/* ── Metric card ─────────────────────────────────────────────────────────────── */
function MetricCard({ m }: { m: Metric }) {
  return (
    <div
      className="metric-card group relative flex h-full flex-col gap-3 rounded-xl border border-border-subtle bg-bg-card p-4 transition-all duration-300 hover:border-border-medium hover:bg-bg-elevated hover:scale-[1.03] hover:-translate-y-1 sm:gap-5 sm:p-6"
      style={{
        transition: "all 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.boxShadow = `0 12px 40px color-mix(in oklab, ${m.accent} 28%, transparent), 0 0 60px color-mix(in oklab, ${m.accent} 14%, transparent)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Accent rail — expands on hover */}
      <span
        aria-hidden
        className="absolute left-0 top-4 h-6 w-[2px] rounded-r-full transition-all duration-300 group-hover:h-10 sm:top-6 sm:h-8 sm:group-hover:h-12"
        style={{ background: m.accent }}
      />

      <p className="pl-2 text-[10px] font-bold uppercase tracking-[0.14em] text-text-muted sm:pl-3 sm:text-[11px] sm:tracking-[0.18em]">
        {m.eyebrow}
      </p>

      <p className="pl-2 text-3xl font-extrabold leading-none tracking-tight text-white sm:pl-3 sm:text-4xl lg:text-5xl">
        <MetricCounter
          value={m.value}
          decimals={m.decimals ?? 0}
          prefix={m.prefix ?? ""}
          suffix={m.suffix ?? ""}
          compact={m.compact ?? false}
          duration={1800}
        />
      </p>

      <div className="flex-1 pl-2 sm:pl-3">
        <Sparkline data={m.series} accent={m.accent} accentFill={m.accentFill} />
      </div>

      <p className="line-clamp-2 pl-2 text-[11px] leading-snug text-text-muted sm:pl-3 sm:text-sm sm:leading-normal">{m.caption}</p>
    </div>
  );
}

/* ── Section ─────────────────────────────────────────────────────────────────── */
export default function LiveNumbers() {
  const sectionRef = useRef<HTMLElement>(null);
  const isNear = useNearViewport(sectionRef);

  useGSAP(
    () => {
      if (!sectionRef.current || !isNear) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Header entrance
        // Set initial hidden state
        gsap.set(".ln-header", { autoAlpha: 0, y: 30 });
        gsap.set(".metric-card", { autoAlpha: 0, y: 60, rotateX: 12, scale: 0.96 });

        gsap.to(".ln-header", {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          scrollTrigger: {
            trigger: ".ln-header",
            start: "top 88%",
          },
        });

        // 3D card cascade — staggered with rotateX for depth
        ScrollTrigger.batch(".metric-card", {
          start: "top 90%",
          onEnter: (batch) => {
            gsap.to(batch, {
              autoAlpha: 1,
              y: 0,
              rotateX: 0,
              scale: 1,
              stagger: 0.12,
              duration: 0.8,
              ease: "power3.out",
              overwrite: true,
            });
          },
        });
      });
    },
    { scope: sectionRef, dependencies: [isNear] },
  );

  return (
    <section ref={sectionRef} className="section-pad relative bg-bg-base" aria-label="Live infrastructure metrics">
      {/* Multi-tonal glow instead of purple-only */}
      <div aria-hidden className="bg-glow-dual pointer-events-none absolute inset-0 -z-10 opacity-50" />

      <Container>
        <div className="ln-header mb-12 max-w-3xl">
          <TextReveal tag="h2" className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
            What our infra is built to deliver,{" "}
            <span className="gradient-text-cyan">backed by SLA.</span>
          </TextReveal>
          <p className="mt-4 text-lg leading-relaxed text-text-secondary">
            Engineering targets and contractual SLAs across the production tier.
            Real-time per-region telemetry is available on the{" "}
            <Link
              href="/node-status"
              className="text-brand-cyan underline-offset-4 hover:underline"
            >
              public status page
            </Link>{" "}
            and on customer dashboards after deployment.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" style={{ perspective: 1200 }}>
          {METRICS.map((m) => (
            <MetricCard key={m.eyebrow} m={m} />
          ))}
        </div>
      </Container>
    </section>
  );
}
